import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ProductCardActions from "../../../cart/components/addToCarts/ProductCardActions";
import ProductModalContainer from "./ProductModalContainer";
import type { TransformedProduct } from "../../types/productApiTypes";
import { getProductImageSource } from "../../helpers/productImgHelpers";
import { useTranslation } from "react-i18next"; // Import the hook

// Define props interface for ProductCard component
interface ProductCardProps {
  product: TransformedProduct;
  viewMode?: "grid" | "list";
}

// Define props for the skeleton component
interface ProductCardSkeletonProps {
  viewMode?: "grid" | "list";
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewMode = "grid",
}) => {
  const { t } = useTranslation();

  // Check if product is out of stock
  const isOutOfStock = product.stockStatus === "outofstock";

  // Get currency symbol from global themeVariables
  const currencySymbol = window.themeVariables?.currencySymbol || "₺";

  // State to track if image is loaded
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  // State for product modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Get the product image source using the helper
  const imageSource = getProductImageSource(product);

  // Handle image load complete
  const handleImageLoad = (): void => {
    setImageLoaded(true);
  };

  // Open product modal
  const handleQuickView = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className={`product-card product-card-${viewMode}`}
        data-product-id={product.id}
      >
        <a href={product.permalink} className="product-link">
          <div
            className={`product-image ${
              imageLoaded ? "image-loaded" : "image-loading"
            }`}
          >
            {/* Add hidden placeholder to maintain space */}
            <div className="image-placeholder"></div>

            <img
              src={imageSource}
              alt={product.name}
              onLoad={handleImageLoad}
              loading="lazy" // Add native lazy loading
              className={imageLoaded ? "visible" : ""}
            />
            {product.onSale && (
              <span className="sale-badge sale-badge-green">
                {t("common.sale")}!
              </span>
            )}
            {isOutOfStock && (
              <span className="stock-badge out-of-stock-badge">
                {t("product.outOfStock")}
              </span>
            )}
            {/* Add a Variable product badge */}
            {/*             {product.isVariable && (
              <span className="variable-badge">
                {t("product.variable")}
              </span>
            )} */}

            {/* Quick view button */}
            <div className="quick-view-wrapper">
              <button
                className="quick-view-btn"
                onClick={handleQuickView}
                title={t("quickView.title")}
                aria-label={t("quickView.title")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="product-content">
            <h2 className="product-title">{product.name}</h2>

            {/* Custom price display instead of dangerouslySetInnerHTML */}
            <div className="product-price">
              {product.onSale ? (
                <>
                  <span className="regular-price">
                    {isOutOfStock
                      ? product.formattedPrice
                      : product.formattedRegularPrice}
                    {currencySymbol}
                  </span>
                  <span className="sale-price">
                    {product.formattedSalePrice}
                    {currencySymbol}
                  </span>
                </>
              ) : (
                <span className="regular-price current-price">
                  {isOutOfStock
                    ? product.formattedPrice
                    : product.formattedRegularPrice}
                  {currencySymbol}
                </span>
              )}
            </div>

            {/* Show short description in list view */}
            {viewMode === "list" && product.shortDescription && (
              <div
                className="product-short-description"
                dangerouslySetInnerHTML={{ __html: product.shortDescription }}
              />
            )}
          </div>
        </a>

        {/* Render the new actions component */}
        <ProductCardActions product={product} />
      </div>

      {/* Product Modal */}
      <ProductModalContainer
        productId={product.id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

// Create a skeleton version of the ProductCard
const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({
  viewMode = "grid",
}) => {
  return (
    <div
      className={`product-card product-card-skeleton product-card-${viewMode}`}
    >
      <div className="product-link">
        <div className="product-image">
          <Skeleton height="100%" width="100%" />
        </div>
        <h2 className="product-title">
          <Skeleton height={24} width="80%" />
        </h2>
        <div className="product-price">
          <Skeleton height={22} width="50%" />
        </div>
      </div>

      <div className="product-card-actions">
        <Skeleton height={36} width="100%" />
        <Skeleton height={36} width="100%" />
      </div>
    </div>
  );
};

// Define the type for the component with the Skeleton property
type ProductCardComponent = React.FC<ProductCardProps> & {
  Skeleton: React.FC<ProductCardSkeletonProps>;
};

// Cast the component to include the Skeleton property
(ProductCard as ProductCardComponent).Skeleton = ProductCardSkeleton;

// Export the component with its proper type
export default ProductCard as ProductCardComponent;
